import { StoreApi } from "zustand";

export interface AsyncDataStoreState<DataT> {
    /**
     * Loaded data (or undefined when it hasn't been loaded yet)
     */
    data?: DataT;
    /**
     * The promise to load the data (internal, for suspense support)
     */
    _dataPromise?: Promise<DataT>;
    /**
     * Is the data still loading
     */
    loading: boolean;
    /**
     * The error of loading the data
     */
    error?: unknown;
    /**
     * Start fetching the data from the API if it isn't loading yet.
     * Return the data when it's available.
     * The method is compatible with Suspense.
     */
    fetchData: (loader: () => Promise<DataT>) => DataT;
    /**
     * Replace the stored data with the new data, provided as a value.
     */
    setData: (data: DataT) => void;
    /**
     * Replace the stored data with the new data, calculated based on the current state.
     */
    modifyData: (getNewData: (currentData: DataT) => DataT) => void;
    /**
     * Invalidate the data loading state.
     * The next fetchData() call from any component will reload the data again.
     *
     * Pass keepData = true to keep displaying the existing data while reloading.
     */
    invalidate: (keepData?: boolean) => void;
}

/**
 * Zustand store to load and manage data that could be loaded asynchronously.
 */
export const getAsyncDataStoreState = <DataT, StateT extends AsyncDataStoreState<DataT> = AsyncDataStoreState<DataT>>(
    set: StoreApi<StateT>["setState"],
    get: StoreApi<StateT>["getState"]
): AsyncDataStoreState<DataT> => ({
    data: undefined,
    _dataPromise: undefined,
    loading: false,
    error: undefined,

    fetchData: (loader) => {
        let { _dataPromise } = get();

        // Fetch the data from the API if we didn't start it yet
        if (!_dataPromise) {
            _dataPromise = loader();

            set({
                _dataPromise,
                loading: true,
                error: undefined,
            } as Partial<StateT>);

            _dataPromise
                .then((data) =>
                    set({
                        data,
                        loading: false,
                    } as Partial<StateT>)
                )
                .catch((error) =>
                    set({
                        error,
                        loading: false,
                    } as Partial<StateT>)
                );
        }

        const { data, error } = get();
        if (error) {
            throw error;
        }
        if (!data) {
            throw _dataPromise;
        }
        return data;
    },

    setData: (data) => {
        set({ data } as Partial<StateT>);
    },

    modifyData: (getNewData) => {
        const { data, setData } = get();
        if (data === undefined) {
            throw new Error("Data was not loaded yet");
        }

        const newData = getNewData(data);
        setData(newData);
    },

    invalidate: (keepData = false) => {
        const updates = {
            _dataPromise: undefined,
            loading: false,
            error: undefined,
        } as Partial<StateT>;

        if (!keepData) {
            updates.data = undefined;
        }

        set(updates);
    },
});
